import React, { lazy, Suspense, useEffect, useRef, useState } from 'react'
import ReactGA from 'react-ga'
import { includesCaseInsensitive } from 'utils/string'
import EnterpriseIcon, {
  PlusCircleIcon,
  ChevronDownIcon,
  CaretDownIcon,
  ErrorFilledIcon,
  FlagFilledIcon,
} from '@enterprise-ui/icons'
import {
  Button,
  Chip,
  Dropdown,
  Grid,
  Heading,
  Modal,
  Spinner,
  Tabs,
  Tooltip,
  useToaster,
} from '@enterprise-ui/canvas-ui-react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useLocation, useParams } from 'react-router-dom'
import Classnames from 'classnames'
import { MODAL_TYPES } from 'config/constants'
import BasePage from 'components/pages/BasePage/BasePage'
import FileTable from './FileTable'
import { IDeleteModalData } from 'components/modals/DeleteModal/DeleteModal'
import { IAssetModalValues } from 'components/modals/EditAssetModal/EditAssetModal'
import styles from './AssetDetailPage.module.scss'
import { IEditAssetManagerModalValues } from 'components/modals/EditAssetManagerModal/EditAssetManagerModal'
import { IAssetActivityValues } from 'components/modals/AssetActivityModal/AssetActivityModal'
import { useElementSize } from 'hooks'
import DetailView from './DetailView/DetailView'
import TagView from './TagView/TagView'
import {
  addCrumb,
  BreadCrumbPriority,
  IAsset,
  IAssetTaxonomy,
  IBreadCrumb,
  ILocationState,
  IUser,
  PageStatus,
  removeCrumb,
  useAsset,
  useAuth,
  useBreadCrumbs,
  useFiles,
  useModal,
} from 'store'
import LoadingState from 'components/shared/LoadingState'
import ErrorState from 'components/shared/ErrorState'
import { useEnv } from '@praxis/component-runtime-env'
import LinkedAssetTable from './LinkedAssets/LinkedAssetTable'
import { useDispatch } from 'react-redux'
import { FileView } from '@galleryjs/api-digital-assets'
import { PermissionView } from 'gallerydigitalassets-v1-client'

// Lazy loading MediaView Component to decouple it's dependencies from our main js bundle.
const MediaViewer = lazy(() => import('./MediaViewer/MediaViewer'))

const TAB_NAMES = {
  FILES: 'files',
  DETAILS: 'details',
  LINKED_ASSETS: 'linked assets',
}

const AssetDetailPage = () => {
  const { id } = useParams()
  const { userSpecialPermissions } = useEnv()
  const location = useLocation()
  const mediaViewerContainer = useRef(null)
  const { width: containerWidth } = useElementSize(mediaViewerContainer)
  const { crumbsFromSlice } = useBreadCrumbs()
  const dispatch = useDispatch()
  const makeToast = useToaster()

  const [activeTab, setActiveTab] = useState<string>(TAB_NAMES.DETAILS)
  const {
    getAsset,
    getAssetUsers,
    setAsset: setAssetInStore,
    deleteAsset,
    assetDetailPageAsset: asset,
    assetDetailPageStatus: pageStatus,
    setAssetDetailStatus,
    userAssetPermissions,
    downloadAssetLocally,
    downloadAsset,
    downloadLinkedAssets,
    sendAssetToGD,
    getAssetStatus,
    process3DFile,
    assetReports,
    getAssetReports,
    resetAssetReports,
    resetUsers,
    assetUsers,
  } = useAsset()
  const { resetFileStore, initializeCheckedFiles, selectAll, downloadMultipleFiles, checkedFileIds } = useFiles()
  const { saveDataAndTypeToStore } = useModal()
  const { canUserCreateContent, isAdmin, userInfo } = useAuth()
  const isUserCreator = canUserCreateContent()
  const isUserAdmin = isAdmin()
  const [assetTitle, setAssetTitle] = useState<string>('')
  const [assetStatus, setAssetStatus] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [hasInvalidFiles, setHasInvalidFiles] = useState<boolean>(false)
  const [crumb, setCrumb] = useState<IBreadCrumb | {}>({})
  const locationState = location.state as unknown as ILocationState

  // Clears any other asset pages from breadcrumbs
  // TODO: Decide if this fix should be on a per page basis or if we should implement this app wide.

  //Checks for Invalid files
  const checkForInvalidFiles = (files: FileView[]) => {
    const invalidFiles = files.filter(file => file.validationResult !== undefined && file.validationResult.length > 0)
    if (invalidFiles.length > 0) {
      setHasInvalidFiles(true)
    }
  }

  const getNewBreadCrumbs = (assetTaxonomy: IAssetTaxonomy) => {
    let current = assetTaxonomy
    while (current.parent) {
      if (current.parent.name === 'Materials' || current.parent.name === 'Scenes' || current.parent.name === 'Models') {
        return [
          {
            id: current.parent.id,
            name: current.parent.name,
            href: `/category/${current.parent.name.toLowerCase()}`,
            priority: BreadCrumbPriority.High,
          },
          {
            id: current.id,
            name: current.name,
            href: `/subcategory/${current.name.toLowerCase()}`,
            priority: BreadCrumbPriority.Low,
          },
        ]
      }
      current = current.parent
    }
    return null
  }

  useEffect(() => {
    crumbsFromSlice?.forEach(crumb => {
      if (includesCaseInsensitive(crumb.href, '/asset/') && crumb.href !== location.pathname && asset) {
        if (locationState?.state?.isLinkedAsset) dispatch(removeCrumb(crumb.name))
      }
    })
    if (asset) {
      getAssetUsers(asset.id, 'EDIT')
      setAssetTitle(asset.name)
      setCrumb({ name: asset.name, href: '', priority: BreadCrumbPriority.VeryLow })
      checkForInvalidFiles(Array.from(asset.files))
      initializeCheckedFiles(Array.from(asset.files))
      setAssetStatus(getAssetStatus(asset.assetTags, asset.taxonomyId))

      // Logic to get updated breadcrumb after Editing an asset's taxonomy
      const newBreadCrumbs = getNewBreadCrumbs(asset.assetTaxonomy) || []

      const updatedCrumbs = (() => {
        if (!Array.isArray(newBreadCrumbs) || newBreadCrumbs.length === 0) return crumbsFromSlice // Ensure newBreadCrumbs is valid
        const materialsIndex = crumbsFromSlice.findIndex(
          crumb => crumb.name === 'Materials' || crumb.name === 'Scenes' || crumb.name === 'Models'
        )
        if (!newBreadCrumbs.length) return crumbsFromSlice // No update needed
        const nextIndex = materialsIndex + 1
        if (crumbsFromSlice.length > 0) {
          const lastCrumb = crumbsFromSlice[crumbsFromSlice.length - 1]
          // IF logic when the last crumb is an asset but its parent is Home
          // ELSE logic when the last crumb is an asset but its parent is Taxonomy category
          if (
            materialsIndex === -1 &&
            lastCrumb.priority === BreadCrumbPriority.VeryLow &&
            lastCrumb.name !== asset.name
          ) {
            dispatch(removeCrumb(lastCrumb.name))
          } else if (lastCrumb.priority === BreadCrumbPriority.VeryLow && lastCrumb.name !== asset.name) {
            return [...crumbsFromSlice.slice(0, materialsIndex), ...newBreadCrumbs]
          }
        }
        return [...crumbsFromSlice.slice(0, materialsIndex), ...newBreadCrumbs, ...crumbsFromSlice.slice(nextIndex + 1)]
      })()

      // Display the new breadcrumbs
      const scenesIndex = crumbsFromSlice.findIndex(
        crumb => crumb.name === 'Scenes' || crumb.name === 'Models' || crumb.name === 'Materials'
      )
      if (scenesIndex !== -1) {
        // Step 1: Remove all breadcrumbs from Scenes"/"Materials"/"Models" onwards (including them itself)
        crumbsFromSlice.slice(scenesIndex).forEach(crumb => {
          dispatch(removeCrumb(crumb.name))
        })
        // Step 2: Find the index of "Scenes"/"Materials"/"Models" in `updatedCrumbs`
        const scenesCrumbIndex = updatedCrumbs.findIndex(
          crumb => crumb.name === 'Scenes' || crumb.name === 'Models' || crumb.name === 'Materials'
        )
        if (scenesCrumbIndex !== -1) {
          // Step 3: Collect new breadcrumbs (including "Scenes"/"Materials"/"Models" itself)
          const newCrumbs = updatedCrumbs.slice(scenesCrumbIndex)
          // Step 4: Add all new breadcrumbs at once
          dispatch(addCrumb(newCrumbs)) // Add the whole array
        }
      }
      // resets asset users so old asset users wont show on new page
      return () => {
        resetUsers()
      }
    }
  }, [asset])

  // fetches asset on component loading from url params
  useEffect(() => {
    if (id) {
      getAsset(id)
      getAssetReports(id)
    }
    // resets assetState so old asset wont show on new page
    return () => {
      setAssetInStore(undefined)
      resetFileStore()
      setAssetDetailStatus(PageStatus.idle)
      trackAndSetActiveTab(TAB_NAMES.DETAILS)
      resetAssetReports()
    }
  }, [id])

  const viewAssetVisibilityAccess = () => {
    if (asset) {
      const assetVisibilityAccess = assetUsers.filter(
        (user: IUser) => user.access === PermissionView.AccessEnum.AssetCreator
      )
      return assetVisibilityAccess.length > 0
    }
    return false
  }

  const trackAndSetActiveTab = (tab: string) => {
    ReactGA.event({
      category: 'Asset Detail',
      action: 'Tab Clicked',
      label: tab,
    })

    if (tab !== 'more') setActiveTab(tab)
  }

  const errorState = () => {
    return <ErrorState name={'Asset'} />
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const confirmModal = () => {
    const assetId = asset?.id ? [asset.id] : []
    process3DFile(assetId)
    makeToast({
      heading: 'Re-processing has begun',
      message: '',
    })
    closeModal()
  }

  const assetDetailPage = () => {
    return (
      <DndProvider backend={HTML5Backend}>
        <BasePage crumbs={crumb}>
          <Grid.Container
            direction="column"
            className={`${styles['content-container']} hc-pa-lg`}
            data-testid="content-container"
          >
            <Grid.Item>
              <Grid.Container direction="row" align="center">
                <Grid.Item xs={12} lg={4} className="hc-pb-dense">
                  <Grid.Container direction="row" align="center">
                    <Grid.Item className={'hc-pr-md'}>
                      <Heading data-testid="title">{assetTitle}</Heading>
                    </Grid.Item>
                    {assetStatus !== '' && (
                      <Grid.Item className={'hc-pl-none'}>
                        <Chip
                          data-testid={`asset-status-chip-${assetStatus}`}
                          size={'dense'}
                          color={assetStatus === 'Active' ? 'success' : 'error'}
                        >
                          {assetStatus}
                        </Chip>
                      </Grid.Item>
                    )}
                    {assetReports?.some(report => !report.resolvedByUserName) && (
                      <Grid.Item className={'hc-pr-md'}>
                        <Tooltip
                          content="There are active issues that have been reported. Click for more info."
                          location="top"
                        >
                          <Button
                            iconOnly
                            type="ghost"
                            onClick={() => {
                              const modalData: IAssetActivityValues = {
                                assetId: asset!.id,
                                activeTab: 'reportedIssues',
                              }
                              saveDataAndTypeToStore(MODAL_TYPES.ASSET_ACTIVITY, modalData)
                            }}
                          >
                            <EnterpriseIcon
                              icon={FlagFilledIcon}
                              size="lg"
                              style={{ color: 'orangered' }}
                              className="pulse"
                            />
                          </Button>
                        </Tooltip>
                      </Grid.Item>
                    )}
                  </Grid.Container>
                </Grid.Item>
                <Grid.Item xs={12} lg={8}>
                  <Grid.Container direction="row" justify="flex-end" align="center">
                    {isUserCreator && (
                      <Grid.Item className="hc-pr-none">
                        <Button
                          type="secondary"
                          className="hc-mr-dense"
                          data-testid="add-to-collection-button"
                          onClick={() => {
                            saveDataAndTypeToStore(MODAL_TYPES.ADD_COLLECTION, [asset])
                          }}
                        >
                          <EnterpriseIcon icon={PlusCircleIcon} className="hc-pr-dense" />
                          Add To Collection
                        </Button>
                      </Grid.Item>
                    )}
                    <Grid.Item className="hc-pl-none">
                      <Dropdown location="bottom-right">
                        <Button type="primary" data-testid="asset-download-dropdown-button">
                          Download <EnterpriseIcon className="hc-pl-dense" icon={ChevronDownIcon} />
                        </Button>
                        <Dropdown.Menu>
                          {selectAll.status === 0 || selectAll.status === 1 ? (
                            <Dropdown.MenuItem
                              onClick={(e: React.MouseEvent<HTMLSpanElement>) => downloadAssetLocally(e, asset!!.id)}
                              data-testid="asset-download-button-full"
                            >
                              Download all files
                            </Dropdown.MenuItem>
                          ) : (
                            <Dropdown.MenuItem
                              onClick={(e: React.MouseEvent<HTMLSpanElement>) =>
                                downloadMultipleFiles(asset!!.id, checkedFileIds())
                              }
                              data-testid="asset-download-button-partial"
                            >
                              Download selected files
                            </Dropdown.MenuItem>
                          )}
                          {!!asset?.linkedAssets && (
                            <Dropdown.MenuItem
                              data-testid="asset-download-dropdown-asset-and-linked-assets"
                              onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                                downloadLinkedAssets(id!)
                              }}
                            >
                              This and all linked assets
                            </Dropdown.MenuItem>
                          )}

                          <Dropdown.MenuItem
                            data-testid="asset-download-dropdown-primary-asset-only"
                            onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                              downloadAsset(id!)
                            }}
                          >
                            This asset only
                          </Dropdown.MenuItem>
                          {selectAll.status === 0 || selectAll.status === 1 ? (
                            <Dropdown.MenuItem
                              data-testid="asset-download-dropdown-local-download-full"
                              onClick={(e: React.MouseEvent<HTMLSpanElement>) => downloadAssetLocally(e, asset!!.id)}
                            >
                              Download asset locally
                            </Dropdown.MenuItem>
                          ) : (
                            <Dropdown.MenuItem
                              data-testid="asset-download-dropdown-local-download-partial"
                              onClick={(e: React.MouseEvent<HTMLSpanElement>) =>
                                downloadMultipleFiles(asset!!.id, checkedFileIds())
                              }
                            >
                              Download locally
                            </Dropdown.MenuItem>
                          )}
                          {userInfo?.specialPermissions?.includes(userSpecialPermissions?.galleryDesktop?.view) && (
                            <Dropdown.MenuItem
                              data-testid="asset-download-dropdown-send-to-GD"
                              onClick={() => sendAssetToGD(asset!!.id)}
                            >
                              Send asset to Gallery Desktop
                            </Dropdown.MenuItem>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </Grid.Item>
                  </Grid.Container>
                </Grid.Item>
              </Grid.Container>
            </Grid.Item>
            <div className="hc-pa-normal hc-pa-none">
              <Grid.Item xs={12}>
                <Grid.Container direction="row">
                  <Grid.Item xs={12} sm={12} md={12} lg={6} ref={mediaViewerContainer}>
                    {/*MEDIAVIEWER*/}
                    {/* Ref here is to calculate viewer width (containerWidth - padding) for responsive sizing, ref doesn't update on MediaViewer component */}
                    <Grid.Container justify="center" className="hc-pt-xl">
                      <Suspense fallback={<Spinner />}>
                        {
                          <MediaViewer
                            files={asset!!.files}
                            // This edge case should be taken care of with max content width of detail page
                            viewerWidth={containerWidth - 24 < 624 ? containerWidth - 24 : 624}
                          />
                        }
                      </Suspense>
                    </Grid.Container>
                  </Grid.Item>
                  <Grid.Item xs={12} sm={12} md={12} lg={6}>
                    {/*Details Section*/}
                    <Grid.Container direction="column" justify="center" align="flex-end">
                      <Grid.Item xs={12} className="hc-pt-expanded hc-pr-none">
                        <Grid.Container>
                          <Grid.Item xs={asset?.linkedAssets ? 4 : 6}></Grid.Item>
                          <Grid.Item className="hc-ml-dense">
                            <Tabs
                              size="expanded"
                              disableAdaptive
                              activeTab={activeTab}
                              onTabSelect={(_e: any, tab: any) => trackAndSetActiveTab(tab.name)}
                              className={styles['tabs-selector']}
                            >
                              <Tabs.Item name={TAB_NAMES.DETAILS} data-testid="tab-details">
                                Details
                              </Tabs.Item>
                              <Tabs.Item
                                name={TAB_NAMES.FILES}
                                data-testid="tab-files"
                                className={styles['tab-selector']}
                              >
                                Files
                                {hasInvalidFiles && (
                                  <EnterpriseIcon
                                    size="inline"
                                    icon={ErrorFilledIcon}
                                    className="hc-pl-dense hc-clr-error pulse-file-validation"
                                  />
                                )}
                              </Tabs.Item>
                              {!!asset?.linkedAssets && (
                                <Tabs.Item
                                  name={TAB_NAMES.LINKED_ASSETS}
                                  data-testid="tab-linked-assets"
                                  className={styles['tab-selector']}
                                >
                                  Linked assets
                                </Tabs.Item>
                              )}
                              <Tabs.Item name="more" className="hc-pa-none">
                                <Dropdown
                                  size="dense"
                                  location="bottom-right"
                                  className={`${styles['more-dropdown-menu']}`}
                                >
                                  <div className="hc-pl-expanded hc-pt-normal hc-pb-expanded hc-pr-lg">
                                    <span>More</span>
                                    <EnterpriseIcon
                                      icon={CaretDownIcon}
                                      className={`hc-mb-dense ${styles['more-tab-arrow']}`}
                                    />
                                  </div>
                                  <Dropdown.Menu>
                                    {(isUserAdmin ||
                                      userAssetPermissions.userCanEditAndDeleteAsset ||
                                      userAssetPermissions.userIsAssetManager) && (
                                      <>
                                        <Dropdown.MenuItem
                                          data-testid="drop-down-item-add-to-collection"
                                          onClick={() => saveDataAndTypeToStore(MODAL_TYPES.ADD_COLLECTION, [asset])}
                                        >
                                          Add to Collection
                                        </Dropdown.MenuItem>
                                        <Dropdown.MenuItem
                                          data-testid="drop-down-item-edit-asset"
                                          onClick={(e: Event) => {
                                            e.preventDefault()

                                            const findIsAssetPrivate = (asset: IAsset) => {
                                              const belongsToPublicOrg =
                                                Array.from(asset.assetOrganizations).filter(
                                                  organization =>
                                                    organization.organization?.id ===
                                                    'cccccccc-aaaa-0000-ffff-000000000001'
                                                ).length > 0
                                              return !belongsToPublicOrg
                                            }

                                            const modalData: IAssetModalValues = {
                                              id: asset!!.id,
                                              name: asset!!.name,
                                              description: '',
                                              isPrivate: findIsAssetPrivate(asset!!),
                                              files: asset!!.files,
                                              assetTaxonomy: asset!!.assetTaxonomy,
                                              taxonomyId: asset!!.assetTaxonomy.id,
                                            }
                                            saveDataAndTypeToStore(MODAL_TYPES.EDIT_ASSET, modalData)
                                          }}
                                        >
                                          Edit asset
                                        </Dropdown.MenuItem>
                                      </>
                                    )}
                                    {viewAssetVisibilityAccess() && (
                                      <Dropdown.MenuItem
                                        data-testid="drop-down-item-view-asset-manager"
                                        onClick={(e: Event) => {
                                          const modalData: IEditAssetManagerModalValues = {
                                            assetId: asset!.id!!,
                                            user_to_be_added: '',
                                            user_to_be_removed: '',
                                          }
                                          saveDataAndTypeToStore(MODAL_TYPES.EDIT_ASSET_USER, modalData)
                                        }}
                                      >
                                        View asset manager
                                      </Dropdown.MenuItem>
                                    )}
                                    <Dropdown.MenuItem
                                      data-testid="drop-down-item-re-process-3d-file"
                                      onClick={() => setIsModalOpen(true)}
                                    >
                                      Re-process 3D file
                                    </Dropdown.MenuItem>
                                    <Dropdown.MenuItem
                                      data-testid="drop-down-item-view-activity-log"
                                      onClick={() => {
                                        const modalData: IAssetActivityValues = {
                                          assetId: asset!.id,
                                        }
                                        saveDataAndTypeToStore(MODAL_TYPES.ASSET_ACTIVITY, modalData)
                                      }}
                                    >
                                      View activity log
                                    </Dropdown.MenuItem>
                                    <Dropdown.MenuItem
                                      data-testid="drop-down-item-report-activity"
                                      onClick={() => {
                                        const modalData: IAssetActivityValues = {
                                          assetId: asset!.id,
                                        }
                                        saveDataAndTypeToStore(MODAL_TYPES.ASSET_REPORT, modalData)
                                      }}
                                    >
                                      Report Asset
                                    </Dropdown.MenuItem>
                                    {(isUserAdmin ||
                                      userAssetPermissions.userCanEditAndDeleteAsset ||
                                      userAssetPermissions.userIsAssetManager) && (
                                      <Dropdown.MenuItem
                                        data-testid="drop-down-item-delete-asset"
                                        onClick={(e: Event) => {
                                          e.preventDefault()
                                          const modalData: IDeleteModalData = {
                                            data: asset!.id,
                                            triggerDelete: () => deleteAsset(asset!!.id),
                                            bodyText: `"${assetTitle}" will be permanently deleted from Gallery.`,
                                            onRefuseText: 'Cancel',
                                            onApproveText: 'Delete Asset',
                                            headingText: 'Delete this asset?',
                                          }
                                          saveDataAndTypeToStore(MODAL_TYPES.DELETE_ASSET, modalData)
                                        }}
                                      >
                                        Delete asset
                                      </Dropdown.MenuItem>
                                    )}
                                  </Dropdown.Menu>
                                </Dropdown>
                              </Tabs.Item>
                            </Tabs>
                          </Grid.Item>
                        </Grid.Container>
                      </Grid.Item>
                      <Grid.Item
                        xs={12}
                        data-testid="tabs-content-files"
                        className={`${styles['data-container']} hc-pt-none ${Classnames(styles['tabs__content'], {
                          [styles.hidden]: activeTab !== TAB_NAMES.FILES,
                        })}`}
                      >
                        <FileTable key={asset!!.id} />
                      </Grid.Item>
                      <Grid.Item
                        xs={12}
                        data-testid="tabs-content-details"
                        className={`${styles['data-container']} ${Classnames(styles['tabs__content'], {
                          [styles.hidden]: activeTab !== TAB_NAMES.DETAILS,
                        })}`}
                      >
                        <Grid.Container direction="column">
                          <Grid.Item>
                            <DetailView assetId={asset!!.id} />
                          </Grid.Item>
                          <Grid.Item>
                            <div
                              className="hc-pa-normal hc-pt-none"
                              data-testid="empty-detail-drawers custom-detail-tags"
                            >
                              <TagView assetId={asset!!.id} />
                            </div>
                          </Grid.Item>
                        </Grid.Container>
                      </Grid.Item>
                      {!!asset?.linkedAssets && (
                        <Grid.Item
                          xs={12}
                          data-testid="tabs-content-linked-assets"
                          className={`${styles['data-container']} ${Classnames(styles['tabs__content'], {
                            [styles.hidden]: activeTab !== TAB_NAMES.LINKED_ASSETS,
                          })}`}
                        >
                          <LinkedAssetTable />
                        </Grid.Item>
                      )}
                    </Grid.Container>
                  </Grid.Item>
                </Grid.Container>
              </Grid.Item>
            </div>
          </Grid.Container>
          <Modal
            isVisible={isModalOpen}
            size="dense"
            headingText="Re-process 3D file"
            data-testid="re-process-confirmation-modal"
            onRefuse={() => closeModal()}
          >
            <div className="hc-pa-normal">
              <Grid.Container>
                <Grid.Item xs>
                  <p>It can take up to several hours to reprocess a file, but will not affect your use of Gallery.</p>
                </Grid.Item>
              </Grid.Container>
              <Grid.Container direction="row-reverse" spacing="dense">
                <Grid.Item>
                  <Button
                    data-testid="re-process-confirmation-modal-confirm"
                    onClick={() => confirmModal()}
                    type="primary"
                  >
                    Confirm
                  </Button>
                </Grid.Item>
                <Grid.Item>
                  <Button
                    data-testid="re-process-confirmation-modal-cancel"
                    onClick={() => closeModal()}
                    type="secondary"
                  >
                    Cancel
                  </Button>
                </Grid.Item>
              </Grid.Container>
            </div>
          </Modal>
        </BasePage>
      </DndProvider>
    )
  }

  // TODO: Determine how we can replace this with React.Suspense
  switch (pageStatus) {
    case PageStatus.idle:
    case PageStatus.loading:
      return <LoadingState crumb={crumb} />
    case PageStatus.error:
      return errorState()
    case PageStatus.success:
      return !asset ? errorState() : assetDetailPage()
  }
}

export default AssetDetailPage
